import * as proto from 'src/proto/compiled-protos';
import { ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { IndeterminatePaginatorComponent } from 'src/app/general/components/indeterminate-paginator/indeterminate-paginator.component';
import { LoadableContentComponent } from 'src/app/general/components/loadable-content/loadable-content.component';
import { IndeterminatePaginatorData, IndeterminatePaginatorModel } from 'src/app/general/components/indeterminate-paginator/indeterminate-paginator-model';
import { Formatter } from '../../util/formatter';
import { SessionService } from '../../services/session.service';
import { LocationsModel } from './locations-model';
import { DataTableComponent } from 'src/app/general/components/data-table/data-table.component';
import { CompactTextColumn, DataTableColumn, LinkColumn, StringColumn } from 'src/app/general/components/data-table/data-table-column';
import { ComponentUtil } from 'src/app/general/util/component-util';
import { NavigationService } from '../../services/navigation.service';
import { BackendService } from '../../services/backend.service';
import { DialogService } from 'src/app/general/services/dialog.service';
import { ToastService } from 'src/app/general/services/toast.service';
import { LoadingController } from 'src/app/general/components/loadable-content/loading-controler';
import { ErrorResult, LoadingMessage } from 'src/app/general/util/result';
import { LocationToolbarModel } from '../../model/location-toolbar-model';
import { Util } from 'src/app/general/util/util';

// ----------------------------------------------------------------------------
// Usage Example

// Template:

// To reset the locations just change the locations model instance
// <app-locations [locationsModel]="myLocationsModelImplementation"></app-locations>
// Example:
// <app-locations [locationsModel]="locationPaidLocationsModel"></app-locations>
// ----------------------------------------------------------------------------

@Component({
  selector: 'app-locations',
  standalone: true,
  imports: [
    CommonModule,
    IndeterminatePaginatorComponent,
    TranslateModule,
    LoadableContentComponent,
    MatCardModule,
    DataTableComponent
  ],
  templateUrl: './locations.component.html',
  styleUrls: ['./locations.component.css']
})
export class LocationsComponent implements IndeterminatePaginatorModel<proto.waiternow.common.ILocationProto>, OnInit, OnChanges {
  @Input() locationsModel!: LocationsModel;

  locationsLoadingController: LoadingController;

  locations: Array<proto.waiternow.common.ILocationProto>;
  initialPaginatorData: IndeterminatePaginatorData<proto.waiternow.common.ILocationProto>;
  paginatorModel: IndeterminatePaginatorModel<proto.waiternow.common.ILocationProto>;
  columns: Array<DataTableColumn<proto.waiternow.common.ILocationProto>>;

  locationToolbarModel: LocationToolbarModel;

  constructor(
      public sessionService: SessionService,
      private navigationService: NavigationService,
      private backendService: BackendService,
      private dialogService: DialogService,
      private translateService: TranslateService,
      private toastService: ToastService,
      private changeDetectorRef: ChangeDetectorRef) {
    this.locationsLoadingController = new LoadingController();

    this.locations = new Array();
    this.initialPaginatorData = { data: new Array(), continuationToken: ''};
    this.paginatorModel = this;
    this.columns = [];

    this.locationToolbarModel = new LocationToolbarModel(
      this.sessionService,
      this.backendService,
      this.navigationService,
      this.translateService,
      this.dialogService,
      this.toastService,
      /* compactForMobile= */ true,
      /* onLocationDeleted= */ locationId => this.loadInitialData()
    );
  }

  ngOnInit(): void {
    this.columns = [
      new CompactTextColumn(
        /* name= */ 'id',
        /* translateId= */ 'id',
        /* valueExtractor= */  location => location.id,
        /* compactTextLength= */ 6),
      new StringColumn(
        /* name= */ 'businessName',
        /* translateId= */ 'business_name',
        /* valueExtractor= */  location => Util.safeString(location.redundantData?.businessName)),
      new StringColumn(
        /* name= */ 'name',
        /* translateId= */ 'name',
        /* valueExtractor= */  location => location.name),
      new StringColumn(
        /* name= */ 'address',
        /* translateId= */ 'address',
        /* valueExtractor= */  location => Formatter.formatAddress(location.address),
        /* hideInCompactMode= */ true),
      new LinkColumn(
        /* name= */ 'stripeAccount',
        /* translateId= */ 'stripe_account',
        /* urlExtractor= */  location => {
          if (!location.settings?.operationSettings?.stripeAccount?.isEnrolled) {
            return '';
          }
          let waiternowStripeAccountId = 'acct_1McCHgDgcBIMp2BX';
          if (location.address?.structuredAddress?.country == proto.waiternow.common.Country.MEXICO) {
            waiternowStripeAccountId = 'acct_1QJK5bLPaXOW3c7i';
          }
          return 'https://dashboard.stripe.com/' + waiternowStripeAccountId + '/connect/accounts/' + Util.safeString(location.settings.operationSettings.stripeAccount.id);
        },
        /* textExtractor= */  location => {
          if (!location.settings?.operationSettings?.stripeAccount?.isEnrolled) {
            return '';
          }
          return Util.safeString(location.settings.operationSettings.stripeAccount.id);
        },
        /* hideInCompactMode= */ true),
    ];
    const additionalColumns = this.locationsModel.getAdditionalColumns();
    for (let column of additionalColumns) {
      this.columns.push(column);
    }
    this.loadInitialData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (ComponentUtil.bindingChanged('locationsModel', changes)) {
      this.ngOnInit();
    }
  }

  updatePage(page: proto.waiternow.common.ILocationProto[]): void {
    this.locations = page;
    // This is needed because the update may be done async after the result of a backend call, and thus after Angular change detection process.
    this.changeDetectorRef.detectChanges();
  }

  fetchData(continuationToken: string | null | undefined): Observable<IndeterminatePaginatorData<proto.waiternow.common.ILocationProto>> {
    return this.locationsModel.fetchData(continuationToken);
  }

  public loadInitialData(): void {
    this.locationsLoadingController.onLoadingStarted(LoadingMessage.withMessageTranslateId('loading_locations'));
    this.locationsModel.fetchData(/* continuationToken= */ null).subscribe(
      {
        next: paginatorData => {
          this.initialPaginatorData = paginatorData;
          this.locationsLoadingController.onSuccess();
          // This is needed because the update may be done async after the result of a backend call, and thus after Angular change detection process.
          this.changeDetectorRef.detectChanges();
        },
        error: error => {
          this.locationsLoadingController.onError(ErrorResult.withErrorAndMessageTranslateId(error, 'error_loading_locations'));
        }
      }
    );
  }
}
